/**
 * THE TERMS AND CONDITIONS ON A WORK ORDER.
 *
 * WO/OVAL/2026-27/OP003 of 30-06-2026 — the order this module exists to replace
 * — had no labour licence clause, no BOCW registration, no workmen's
 * compensation insurance, no PF or ESI, no safety clause, no liquidated
 * damages, no trigger for releasing its 10% retention, a pay-when-paid sentence
 * that stopped mid-line, and a clause deducting 18% "in case Contractor does
 * not deposit GST" which is simply not the law for an unregistered contractor.
 *
 * Every one of those is a clause somebody forgot to type, because there was
 * nowhere for it to live. Now there is.
 *
 * HOW IT WORKS, AND THE ONE THING THAT MATTERS MOST
 *
 *   The clauses live in a master, held separately per pricing basis — COMMON,
 *   plus one set each for measured items, area rates, lumpsum and day rates.
 *   Raising an order copies COMMON plus the sets its lines actually use.
 *
 *   THE ORDER THEN KEEPS ITS OWN COPY. Editing the master next April must not
 *   silently rewrite a contract signed last October. A work order is evidence
 *   of what was agreed on the day it was signed, and it has to go on saying so
 *   — which is why fn_wo_terms_editable refuses any change once the order has
 *   left DRAFT.
 */
import { useCallback, useEffect, useState } from 'react'
import { FileText, Plus, RefreshCw, Scale, Trash2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { Card, Loading, Textarea, useToast } from '@/components/ui'

export interface WoTerm {
  id: string
  wo_id: string
  sl_no: number
  clause_text: string
  source_basis: string | null
  is_included: boolean
}

const BASIS_LABEL: Record<string, string> = {
  COMMON: 'General',
  MEASURED_ITEM: 'Measured item',
  AREA_RATE: 'Area rate',
  LUMPSUM: 'Lumpsum',
  DAY_RATE: 'Day rate',
  EQUIPMENT_HIRE: 'Equipment hire',
}

export function WoTerms({ woId, editable, onCount }: {
  woId: string
  /** DRAFT or RETURNED, and this person may write the order. */
  editable: boolean
  onCount?: (n: number) => void
}) {
  const toast = useToast()
  const [terms, setTerms] = useState<WoTerm[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [draft, setDraft] = useState<Record<string, string>>({})

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('wo_terms').select('*')
      .eq('wo_id', woId).order('sl_no')
    const rows = (data ?? []) as WoTerm[]
    setTerms(rows)
    setDraft(Object.fromEntries(rows.map((r) => [r.id, r.clause_text])))
    setLoading(false)
    onCount?.(rows.length)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [woId])

  useEffect(() => { void load() }, [load])

  /**
   * Pull the standard clauses in. `replace` is false on the first pull, so a
   * second click never quietly wipes an edit somebody made — the RPC returns
   * the existing count and changes nothing.
   */
  async function apply(replace: boolean) {
    setBusy(true)
    try {
      const { data, error } = await supabase.rpc('rpc_wo_apply_terms',
        { p_wo_id: woId, p_replace: replace })
      if (error) throw new Error(error.message)
      toast.success(replace
        ? `Reset to the standard clauses — ${data} of them.`
        : `${data} clause(s) on this order.`)
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not apply the standard terms')
    } finally { setBusy(false) }
  }

  async function saveOne(t: WoTerm) {
    const text = (draft[t.id] ?? '').trim()
    if (!text || text === t.clause_text) return
    setBusy(true)
    try {
      const { error } = await supabase.from('wo_terms')
        .update({ clause_text: text }).eq('id', t.id)
      if (error) throw new Error(error.message)
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save that clause')
    } finally { setBusy(false) }
  }

  async function addOne() {
    setBusy(true)
    try {
      const next = terms.length ? Math.max(...terms.map((t) => t.sl_no)) + 1 : 1
      const { error } = await supabase.from('wo_terms').insert({
        wo_id: woId, sl_no: next,
        clause_text: 'New clause — type it here.', source_basis: null,
      })
      if (error) throw new Error(error.message)
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not add a clause')
    } finally { setBusy(false) }
  }

  /**
   * Tick a clause on or off for THIS order.
   *
   * Unticking does not delete. An approver looking at a draft should be able to
   * see that a clause was put in front of the buyer and dropped; a deleted row
   * shows nothing at all, and "why is the retention clause missing" is a
   * question somebody will ask at stage three.
   *
   * Only ticked clauses print, and the PDF renumbers 1..n over what remains.
   */
  async function toggleOne(t: WoTerm) {
    setBusy(true)
    try {
      const { error } = await supabase.from('wo_terms')
        .update({ is_included: !t.is_included }).eq('id', t.id)
      if (error) throw new Error(error.message)
      setTerms((ts) => ts.map((x) =>
        x.id === t.id ? { ...x, is_included: !t.is_included } : x))
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not change that clause')
    } finally { setBusy(false) }
  }

  async function removeOne(t: WoTerm) {
    if (!window.confirm(`Delete clause ${t.sl_no} from this order?`)) return
    setBusy(true)
    try {
      const { error } = await supabase.from('wo_terms').delete().eq('id', t.id)
      if (error) throw new Error(error.message)
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not delete that clause')
    } finally { setBusy(false) }
  }

  if (loading) return <Loading label="Reading the terms…" />

  return (
    <Card
      title="Terms and conditions"
      subtitle={editable
        ? `Copied from the standard set for this order’s pricing bases. ${
            terms.filter((t) => t.is_included).length} of ${terms.length} are ticked and will print. `
          + 'Untick what does not apply, edit the wording, or add your own — until the order is submitted.'
        : `This order’s own copy, frozen when it was raised — ${
            terms.filter((t) => t.is_included).length} clause(s) printed. `
          + 'Changing the standard set later does not change what was signed here.'}
      actions={editable ? (
        <div className="flex flex-wrap gap-2">
          {terms.length === 0 ? (
            <button className="btn-primary btn-sm" disabled={busy} onClick={() => void apply(false)}>
              <FileText className="h-4 w-4" /> Bring in the standard terms
            </button>
          ) : (
            <>
              <button className="btn-secondary btn-sm" disabled={busy} onClick={() => void addOne()}>
                <Plus className="h-4 w-4" /> Add a clause
              </button>
              <button className="btn-ghost btn-sm text-slate-600" disabled={busy}
                      title="Throw away every edit on this order and take the standard set again"
                      onClick={() => {
                        if (window.confirm(
                          'Reset to the standard clauses?\n\nEvery edit made on THIS order '
                          + 'will be lost. The standard set itself is not touched.')) {
                          void apply(true)
                        }
                      }}>
                <RefreshCw className="h-4 w-4" /> Reset to standard
              </button>
            </>
          )}
        </div>
      ) : undefined}
    >
      {terms.length === 0 ? (
        <p className="rounded-lg border border-dashed border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
          <strong>This order carries no terms.</strong>{' '}
          {editable
            ? 'Bring in the standard set before submitting it — an order with no conditions is the document this module was built to stop.'
            : 'It was raised before the standard clauses existed.'}
        </p>
      ) : (
        <ol className="space-y-2">
          {terms.map((t) => (
            <li key={t.id} className={t.is_included ? 'flex gap-3' : 'flex gap-3 opacity-45'}>
              {editable && (
                <input type="checkbox" className="mt-2.5 h-4 w-4 shrink-0 rounded border-slate-300"
                       checked={t.is_included} disabled={busy}
                       onChange={() => void toggleOne(t)}
                       aria-label={`Include clause ${t.sl_no} on this order`} />
              )}
              <span className="w-6 shrink-0 pt-2 text-right text-sm font-semibold tabular-nums text-slate-400">
                {t.sl_no}.
              </span>
              <div className="min-w-0 flex-1">
                {editable ? (
                  <Textarea
                    rows={Math.max(2, Math.ceil((draft[t.id] ?? '').length / 110))}
                    value={draft[t.id] ?? ''}
                    onChange={(e) => setDraft((d) => ({ ...d, [t.id]: e.target.value }))}
                    onBlur={() => void saveOne(t)}
                  />
                ) : (
                  <p className="py-1.5 text-sm leading-relaxed text-slate-700">{t.clause_text}</p>
                )}
                <span className="mt-0.5 inline-flex items-center gap-1.5">
                  {t.source_basis && (
                    <span className="pill-grey">
                      {BASIS_LABEL[t.source_basis] ?? t.source_basis}
                    </span>
                  )}
                  {!t.is_included && (
                    <span className="pill-amber">Not on this order</span>
                  )}
                </span>
              </div>
              {editable && (
                <button className="btn-ghost btn-sm mt-1 shrink-0 text-red-600"
                        disabled={busy} onClick={() => void removeOne(t)}
                        aria-label={`Delete clause ${t.sl_no}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
        </ol>
      )}

      {editable && terms.length > 0 && (
        <p className="mt-3 flex items-start gap-1.5 text-[11px] text-slate-400">
          <Scale className="mt-0.5 h-3 w-3 shrink-0" />
          A clause is saved when you click away from it. These are printed on the order the
          contractor signs, and they cannot be changed once it has been submitted.
        </p>
      )}
    </Card>
  )
}
